
import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, getDocs, query, orderBy, doc, updateDoc, deleteDoc, where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

const StudentVerificationList = () => {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && user.email === 'tech@nabi.health') {
        setIsAdmin(true);
        fetchVerifications();
      } else {
        setIsAdmin(false);
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchVerifications = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "student_verifications"), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVerifications(data);
    } catch (error) {
      console.error("Error fetching verifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const verification = verifications.find(v => v.id === id);
      const docRef = doc(db, "student_verifications", id);
      await updateDoc(docRef, { status: newStatus });
      
      // Also update the user's verification status in their user profile
      let userDocId = null;
      if (verification?.userId && verification.userId !== "Guest") {
        const q = query(collection(db, "users"), where("uid", "==", verification.userId));
        const userSnap = await getDocs(q);
        if (!userSnap.empty) {
          userDocId = userSnap.docs[0].id;
        }
      }
      if (!userDocId && verification?.userEmail && verification.userEmail !== "Guest") {
        const qEmail = query(collection(db, "users"), where("email", "==", verification.userEmail));
        const emailSnap = await getDocs(qEmail);
        if (!emailSnap.empty) {
          userDocId = emailSnap.docs[0].id;
        }
      }
      if (userDocId) {
        await updateDoc(doc(db, "users", userDocId), {
          isStudentVerified: newStatus === 'approved',
          studentVerificationStatus: newStatus
        });
      }

      setVerifications(prev => prev.map(v => v.id === id ? { ...v, status: newStatus } : v));
      alert(`Request ${newStatus} successfully.`);
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Error updating status. Check console.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      await deleteDoc(doc(db, "student_verifications", id));
      setVerifications(prev => prev.filter(v => v.id !== id));
    } catch (error) {
      console.error("Error deleting record:", error);
    }
  };

  if (!isAdmin) return null;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Student Verification Requests</h1>
        <p style={styles.subtitle}>Review and manage student ID submissions</p>
      </div>

      {loading ? (
        <div style={styles.loader}>Loading requests...</div>
      ) : verifications.length === 0 ? (
        <div style={styles.empty}>No verification requests found.</div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.thRow}>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>User Email</th>
                <th style={styles.th}>Student ID #</th>
                <th style={styles.th}>Graduation Date</th>
                <th style={styles.th}>ID Card Image</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {verifications.map((v) => {
                let isGraduated = false;
                if (v.graduationDate) {
                  const gradDate = new Date(v.graduationDate);
                  const now = new Date();
                  if (gradDate < now) {
                    isGraduated = true;
                  }
                }
                return (
                  <tr key={v.id} style={styles.tr}>
                    <td style={styles.td}>
                      {v.createdAt?.toDate ? v.createdAt.toDate().toLocaleDateString() : 'N/A'}
                    </td>
                    <td style={styles.td}>{v.userEmail}</td>
                    <td style={styles.td}>{v.idNumber}</td>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '500' }}>
                        {v.graduationDate ? new Date(v.graduationDate).toLocaleDateString() : 'N/A'}
                      </div>
                      {isGraduated && (
                        <div style={{ color: '#E53E3E', fontSize: '11px', fontWeight: 'bold', marginTop: '6px', backgroundColor: '#FED7D7', padding: '4px 8px', borderRadius: '6px', display: 'inline-block' }}>
                          ⚠️ Not a student anymore
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>
                      {v.idCardUrl ? (
                        <a href={v.idCardUrl} target="_blank" rel="noopener noreferrer">
                          <img src={v.idCardUrl} alt="ID Card" style={styles.thumbnail} />
                        </a>
                      ) : 'No Image'}
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.statusBadge,
                        backgroundColor: v.status === 'approved' ? '#C6F6D5' : v.status === 'rejected' ? '#FED7D7' : '#FEEBC8',
                        color: v.status === 'approved' ? '#22543D' : v.status === 'rejected' ? '#742A2A' : '#744210'
                      }}>
                        {v.status || 'pending'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionGroup}>
                        <button 
                          onClick={() => handleStatusUpdate(v.id, 'approved')} 
                          style={{ ...styles.actionBtn, color: '#38A169' }}
                          title="Approve"
                        >
                          ✓
                        </button>
                        <button 
                          onClick={() => handleStatusUpdate(v.id, 'rejected')} 
                          style={{ ...styles.actionBtn, color: '#E53E3E' }}
                          title="Reject"
                        >
                          ✕
                        </button>
                        <button 
                          onClick={() => handleDelete(v.id)} 
                          style={{ ...styles.actionBtn, color: '#718096' }}
                          title="Delete"
                        >
                          🗑
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '120px 40px 40px',
    backgroundColor: '#FFF7F1',
    minHeight: '100vh',
    fontFamily: "'Syne', sans-serif"
  },
  header: {
    marginBottom: '40px',
    textAlign: 'center'
  },
  title: {
    fontSize: '2.5rem',
    color: '#003049',
    marginBottom: '10px'
  },
  subtitle: {
    fontSize: '1.1rem',
    color: '#666'
  },
  loader: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '1.2rem',
    color: '#003049'
  },
  empty: {
    textAlign: 'center',
    padding: '60px',
    background: 'white',
    borderRadius: '24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
    color: '#999',
    fontSize: '1.1rem'
  },
  tableWrapper: {
    background: 'white',
    borderRadius: '24px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
    overflow: 'hidden'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left'
  },
  thRow: {
    backgroundColor: '#F8FAFC',
    borderBottom: '2px solid #EDF2F7'
  },
  th: {
    padding: '20px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  tr: {
    borderBottom: '1px solid #EDF2F7',
    transition: 'background 0.2s'
  },
  td: {
    padding: '20px',
    fontSize: '15px',
    color: '#1A202C',
    verticalAlign: 'middle'
  },
  thumbnail: {
    width: '60px',
    height: '40px',
    objectFit: 'cover',
    borderRadius: '6px',
    cursor: 'pointer',
    border: '1px solid #E2E8F0'
  },
  statusBadge: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'capitalize'
  },
  actionGroup: {
    display: 'flex',
    gap: '15px'
  },
  actionBtn: {
    background: 'none',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '5px',
    transition: 'transform 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
};

export default StudentVerificationList;
